import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { bondTabs } from "../hooks/useApproveBonds";

function getApproveBondsSearchLabel(tab) {
  if (tab === "Data Corrections") return "Search by bond number";
  if (tab?.toLowerCase() === "to be cancelled") return "Search";
  return "Search by Principal";
}

export default function ApproveBondsTabsSearchToolbar({
  tabIndex,
  handleTabChange,
  statusCounts,
  selectedType,
  dataCorrectionsList,
  searchQuery,
  setSearchQuery,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 2,
        justifyContent: "space-between",
        alignItems: "center",
        mb: 2,
      }}
    >
      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
      >
        {bondTabs.map((tab) => {
          const tabKey = tab.toLowerCase();
          const count = statusCounts[tabKey] || 0;
          const displayLabel = tab === "Rejected" ? "Declined" : tab;
          const isToCancel = tabKey === "to be cancelled";
          const isDataCorrections = tab === "Data Corrections";
          const dataCorrectionsNewCount = isDataCorrections
            ? dataCorrectionsList.filter(
                (r) =>
                  (r.bond_type || "").toString().trim() === (selectedType || "").toString().trim() &&
                  (r.status || "").toString().trim().toLowerCase() === "new"
              ).length
            : 0;
          const showPill = !isDataCorrections && count > 0 && tab !== "Approved" && tab !== "Rejected";
          const showDataCorrectionsNewChip = isDataCorrections && dataCorrectionsNewCount > 0;

          return (
            <Tab key={tab} label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {displayLabel}
                  </Typography>
                  {showPill && (
                    <Chip
                      size="small"
                      color={isToCancel ? "error" : "primary"}
                      label={count}
                    />
                  )}
                  {showDataCorrectionsNewChip && (
                    <Chip
                      size="small"
                      color="warning"
                      label={dataCorrectionsNewCount}
                    />
                  )}
                </Stack>
              }
            />
          );
        })}
      </Tabs>

      <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
        <TextField
          label={getApproveBondsSearchLabel(bondTabs[tabIndex])}
          variant="outlined"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            width: { xs: "100%", sm: 360 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
            },
          }}
          slotProps={{ input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="clear search"
                  size="small"
                  onClick={() => setSearchQuery("")}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}}
        />
      </Box>
    </Box>
  );
}

ApproveBondsTabsSearchToolbar.propTypes = {
  tabIndex: PropTypes.number.isRequired,
  handleTabChange: PropTypes.func.isRequired,
  statusCounts: PropTypes.object.isRequired,
  selectedType: PropTypes.string,
  dataCorrectionsList: PropTypes.array.isRequired,
  searchQuery: PropTypes.string.isRequired,
  setSearchQuery: PropTypes.func.isRequired,
};
